import { ApplicationDispatchContext } from 'contexts/ApplicationContext'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'

declare global {
	interface Window {
		dataLayer: Array<any>
	}
}

const GtmTrackingService = () => {
	const { loanApplication }: IApplicationContext = useContext(
		ApplicationDispatchContext
	)
	const [applicationId, setApplicationId] = useState(
		loanApplication.applicationData?.loanNumber
			? loanApplication.applicationData?.loanNumber
			: 0
	)

	const relevantDivIds: Array<RegExp> = useMemo(() => {
		return [
			/^sign-promissory-note-.*-otp-method-button$/,
			/^upload-documents-trashcan$/,
		]
	}, [])

	const pushDataToGtm = useCallback(
		(activeElement: HTMLElement, action: string) => {
			if (applicationId !== 0) {
				window.dataLayer = window.dataLayer || []
				window.dataLayer.push({
					event: 'user_interaction',
					applicationId: applicationId,
					timeStamp: new Date().toISOString(),
					element_id: activeElement.id,
					element_class: activeElement.className,
					element_type: activeElement.tagName.toLowerCase(),
					action: action,
				})
			}
		},
		[applicationId]
	)

	const handleClick = useCallback(
		(event: any) => {
			// We're only interested in button clicks and clicks on certain div elements.
			var relevantTarget = event.target
			const closestButton = relevantTarget.closest('button')
			if (closestButton) {
				relevantTarget = closestButton
			} else {
				const closestDiv = relevantTarget.closest('div')
				if (
					closestDiv?.id &&
					relevantDivIds.some((regexp) => regexp.test(closestDiv.id))
				) {
					relevantTarget = closestDiv
				} else {
					return
				}
			}
			pushDataToGtm(relevantTarget, 'clicked')
		},
		[pushDataToGtm, relevantDivIds]
	)

	const handleChange = useCallback(
		(event: any) => {
			// We're only interested in input elements.
			const target = event.target as HTMLElement
			if (target instanceof HTMLInputElement) {
				pushDataToGtm(
					target,
					target.type === 'checkbox'
						? 'checkbox value changed to: ' + target.checked
						: 'input changed to: ' + target.value
				)
			}
		},
		[pushDataToGtm]
	)

	useEffect(() => {
		setApplicationId(
			loanApplication.applicationData?.loanNumber
				? loanApplication.applicationData?.loanNumber
				: 0
		)
	}, [loanApplication.applicationData?.loanNumber])

	useEffect(() => {
		document.addEventListener('click', handleClick)
		document.addEventListener('change', handleChange)

		return () => {
			document.removeEventListener('click', handleClick)
			document.removeEventListener('change', handleChange)
		}
	}, [handleClick, handleChange])

	return <></>
}

export default GtmTrackingService
